import React, { useState, useEffect } from 'react';
import { config } from '../config';
import Spinner from '../components/Spinner';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import './Settings.css';

function Settings({ tenantId }) {
  const { showNotification } = useNotification();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [sicherungen, setSicherungen] = useState([]);
  const [sicherungSchedule, setSicherungSchedule] = useState({ enabled: true, frequency: 'daily', time: '02:00' });
  const [sicherungName, setSicherungName] = useState('');
  const [showSicherungModal, setShowSicherungModal] = useState(false);
  const [restoreSicherungId, setRestoreSicherungId] = useState(null);
  const [importingSicherung, setImportingSicherung] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    taxSystem: 'DE',
    address: {
      street: '',
      city: '',
      zip: '',
      country: ''
    },
    bankData: {
      bankName: '',
      iban: '',
      bic: ''
    },
    customerSelfServiceEnabled: false,
    logo: null,
    isKleinunternehmer: false,
    isNotVatLiable: false,
    invoiceFooterText: 'Vielen Dank für Ihren Auftrag!',
    imprint: {
      taxNumber: '',
      vatIdNumber: '',
      courtLocation: '',
      managingDirector: '',
      phone: '',
      email: '',
      website: ''
    },
    sicherungRetention: {
      hourly: 7,
      daily: 30,
      weekly: 90,
      monthly: 365
    }
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [showTestDataButton, setShowTestDataButton] = useState(false);
  const [recreatingTestData, setRecreatingTestData] = useState(false);
  const [user, setUser] = useState(null);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [deletionTenantName, setDeletionTenantName] = useState('');
  const [deletionAdminPassword, setDeletionAdminPassword] = useState('');
  const [deletionDelayHours, setDeletionDelayHours] = useState(24);
  const [deletingTenant, setDeletingTenant] = useState(false);

  useEffect(() => {
    loadUser();
    if (tenantId) {
      loadTenant();
      loadSicherungen();
      loadSicherungSchedule();
      checkTestDataEnabled();
    } else {
      setLoading(false);
    }
  }, [tenantId]);

  const loadUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data);
      }
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const checkTestDataEnabled = async () => {
    try {
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/config/testdata-enabled`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setShowTestDataButton(data.enabled || false);
      }
    } catch (error) {
      console.error('Error checking test data config:', error);
    }
  };

  const handleRecreateTestData = async () => {
    if (!window.confirm('Möchten Sie die Testdaten wirklich neu erstellen? Alle vorhandenen Testdaten werden überschrieben!')) {
      return;
    }

    try {
      setRecreatingTestData(true);
      setError(null);
      await api.post(`/tenants/${tenantId}/recreate-testdata`, {}, tenantId);
      setSuccessMessage('Testdaten erfolgreich neu erstellt. Seite wird neu geladen...');
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Error recreating test data:', error);
      setError('Fehler beim Neuerstellen der Testdaten: ' + (error.response?.data?.error || error.message));
      setRecreatingTestData(false);
    }
  };

  const loadTenant = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`/tenants/${tenantId}`, null);
      if (data) {
        setTenant(data);
        setFormData({
          name: data.name || '',
          taxSystem: data.taxSystem || 'DE',
          address: data.address || {
            street: '',
            city: '',
            zip: '',
            country: ''
          },
          bankData: data.bankData || {
            bankName: '',
            iban: '',
            bic: ''
          },
          customerSelfServiceEnabled: data.customerSelfServiceEnabled || false,
          logo: data.logo || null,
          isKleinunternehmer: data.isKleinunternehmer || false,
          isNotVatLiable: data.isNotVatLiable || false,
          invoiceFooterText: data.invoiceFooterText || 'Vielen Dank für Ihren Auftrag!',
          imprint: data.imprint || {
            taxNumber: '',
            vatIdNumber: '',
            courtLocation: '',
            managingDirector: '',
            phone: '',
            email: '',
            website: ''
          },
          sicherungRetention: data.snapshotRetention || {
            hourly: 7,
            daily: 30,
            weekly: 90,
            monthly: 365
          }
        });
        setLogoPreview(data.logo || null);
      } else {
        setError('Tenant nicht gefunden');
      }
    } catch (error) {
      console.error('Error loading tenant:', error);
      setError('Fehler beim Laden der Einstellungen');
      setTenant({ name: '', taxSystem: 'DE', address: {} });
    } finally {
      setLoading(false);
    }
  };

  const getFrequencyLabel = (frequency) => {
    const labels = {
      hourly: 'Stündlich',
      daily: 'Täglich',
      weekly: 'Wöchentlich',
      monthly: 'Monatlich',
      yearly: 'Jährlich'
    };
    return labels[frequency] || frequency;
  };

  const groupedSicherungen = sicherungen.reduce((acc, sicherung) => {
    const frequency = sicherung.frequency || 'daily';
    if (!acc[frequency]) {
      acc[frequency] = [];
    }
    acc[frequency].push(sicherung);
    return acc;
  }, {});

  const frequencies = ['hourly', 'daily', 'weekly', 'monthly', 'yearly'];

  const loadSicherungen = async () => {
    try {
      const data = await api.get('/snapshots', tenantId);
      setSicherungen(data);
    } catch (error) {
      console.error('Error loading sicherungen:', error);
    }
  };

  const loadSicherungSchedule = async () => {
    try {
      const data = await api.get('/snapshots/schedule', tenantId);
      setSicherungSchedule(data);
    } catch (error) {
      console.error('Error loading sicherung schedule:', error);
    }
  };

  const handleTakeSicherung = async () => {
    try {
      setError(null);
      await api.post('/snapshots', { name: sicherungName || null }, tenantId);
      setSicherungName('');
      setShowSicherungModal(false);
      await loadSicherungen();
      setSuccessMessage('Sicherung erfolgreich erstellt');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error creating sicherung:', error);
      setError('Fehler beim Erstellen der Sicherung: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleRestoreSicherung = async (sicherungId) => {
    if (!window.confirm('Möchten Sie diese Sicherung wirklich wiederherstellen? Alle aktuellen Daten werden überschrieben!')) {
      return;
    }
    try {
      setError(null);
      setRestoreSicherungId(sicherungId);
      await api.post(`/snapshots/${sicherungId}/restore`, {}, tenantId);
      setRestoreSicherungId(null);
      setSuccessMessage('Sicherung erfolgreich wiederhergestellt');
      setTimeout(() => setSuccessMessage(null), 3000);
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Error restoring sicherung:', error);
      setError('Fehler beim Wiederherstellen der Sicherung: ' + (error.response?.data?.error || error.message));
      setRestoreSicherungId(null);
    }
  };

  const handleDeleteSicherung = async (sicherungId) => {
    if (!window.confirm('Möchten Sie diese Sicherung wirklich löschen?')) {
      return;
    }
    try {
      setError(null);
      await api.delete(`/snapshots/${sicherungId}`, tenantId);
      await loadSicherungen();
      setSuccessMessage('Sicherung erfolgreich gelöscht');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting sicherung:', error);
      setError('Fehler beim Löschen der Sicherung: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleExportSicherung = async (sicherungId) => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      const apiUrl = config.API_URL || 'https://api.garagedesk.net/api';
      const response = await fetch(`${apiUrl}/snapshots/${sicherungId}/export`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': tenantId
        }
      });
      
      if (!response.ok) {
        throw new Error('Fehler beim Export');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const contentDisposition = response.headers.get('Content-Disposition');
      const fileName = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') 
        : `sicherung_${sicherungId}.tar.gz`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSuccessMessage('Sicherung erfolgreich exportiert');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error exporting sicherung:', error);
      setError('Fehler beim Exportieren der Sicherung: ' + (error.message || 'Unbekannter Fehler'));
    }
  };

  const handleImportSicherung = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.tar.gz') && !file.name.endsWith('.tgz')) {
      setError('Nur .tar.gz Dateien sind erlaubt');
      return;
    }
    
    if (!window.confirm('Möchten Sie diese Sicherung wirklich importieren? Alle aktuellen Daten werden überschrieben!')) {
      e.target.value = '';
      return;
    }
    
    try {
      setError(null);
      setImportingSicherung(true);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      const apiUrl = config.API_URL || 'https://api.garagedesk.net/api';
      const response = await fetch(`${apiUrl}/snapshots/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': tenantId
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Import');
      }
      
      const result = await response.json();
      e.target.value = '';
      await loadSicherungen();
      setSuccessMessage('Sicherung erfolgreich importiert');
      setTimeout(() => setSuccessMessage(null), 3000);
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Error importing sicherung:', error);
      setError('Fehler beim Importieren der Sicherung: ' + (error.message || 'Unbekannter Fehler'));
      e.target.value = '';
    } finally {
      setImportingSicherung(false);
    }
  };

  const handleSaveSchedule = async () => {
    try {
      setError(null);
      await api.put('/snapshots/schedule', sicherungSchedule, tenantId);
      setSuccessMessage('Sicherungs-Zeitplan erfolgreich gespeichert');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error saving schedule:', error);
      setError('Fehler beim Speichern des Zeitplans: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleScheduleDeletion = async () => {
    if (deletionTenantName !== tenant?.name) {
      showNotification('Tenant-Name stimmt nicht überein', 'error');
      return;
    }

    if (!deletionAdminPassword) {
      showNotification('Admin-Passwort ist erforderlich', 'error');
      return;
    }

    try {
      setDeletingTenant(true);
      setError(null);
      await api.post(`/tenants/${tenantId}/schedule-deletion`, {
        tenantName: deletionTenantName,
        adminPassword: deletionAdminPassword,
        delayHours: deletionDelayHours
      }, null);
      showNotification(`Löschung wurde für ${deletionDelayHours} Stunden terminiert`, 'success');
      setShowDeletionModal(false);
      setDeletionTenantName('');
      setDeletionAdminPassword('');
      setDeletionDelayHours(24);
      loadTenant();
    } catch (error) {
      console.error('Error scheduling deletion:', error);
      setError('Fehler beim Terminieren der Löschung: ' + (error.response?.data?.error || error.message));
      showNotification(error.response?.data?.error || error.message || 'Fehler beim Terminieren der Löschung', 'error');
    } finally {
      setDeletingTenant(false);
    }
  };

  const handleCancelDeletion = async () => {
    if (!window.confirm('Möchten Sie die terminierte Löschung wirklich abbrechen?')) {
      return;
    }

    try {
      setError(null);
      await api.post(`/tenants/${tenantId}/cancel-deletion`, {}, null);
      showNotification('Terminierte Löschung wurde abgebrochen', 'success');
      loadTenant();
    } catch (error) {
      console.error('Error cancelling deletion:', error);
      setError('Fehler beim Abbrechen der Löschung: ' + (error.response?.data?.error || error.message));
      showNotification(error.response?.data?.error || error.message || 'Fehler beim Abbrechen der Löschung', 'error');
    }
  };


  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Bitte wählen Sie eine Bilddatei aus');
      return;
    }

    // Validate file size (max 5MB before compression)
    if (file.size > 5 * 1024 * 1024) {
      setError('Die Datei ist zu groß. Maximal 5MB erlaubt.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.onload = () => {
        // Komprimiere das Bild auf max 150x60px für Logo
        const canvas = document.createElement('canvas');
        const maxWidth = 150;
        const maxHeight = 60;
        
        let width = img.width;
        let height = img.height;
        
        // Berechne neue Dimensionen, behalte Seitenverhältnis bei
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Konvertiere zu Base64 mit Kompression (JPEG, Qualität 0.7)
        // Versuche verschiedene Qualitätsstufen, bis die Datei klein genug ist
        let base64String = canvas.toDataURL('image/jpeg', 0.7);
        let quality = 0.7;
        
        // Reduziere Qualität schrittweise, bis Datei unter 1MB (Base64) ist
        while (base64String.length > 1 * 1024 * 1024 && quality > 0.3) {
          quality -= 0.1;
          base64String = canvas.toDataURL('image/jpeg', quality);
        }
        
        // Falls immer noch zu groß, versuche noch kleinere Dimensionen
        if (base64String.length > 1 * 1024 * 1024) {
          const smallerWidth = Math.max(100, width * 0.8);
          const smallerHeight = Math.max(40, height * 0.8);
          canvas.width = smallerWidth;
          canvas.height = smallerHeight;
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, smallerWidth, smallerHeight);
          base64String = canvas.toDataURL('image/jpeg', 0.6);
        }
        
        // Finale Prüfung
        if (base64String.length > 1.5 * 1024 * 1024) {
          setError('Bild ist zu groß auch nach Kompression. Bitte wählen Sie ein kleineres Bild.');
          return;
        }
        
        setFormData({ ...formData, logo: base64String });
        setLogoPreview(base64String);
      };
      img.onerror = () => {
        setError('Fehler beim Laden des Bildes');
      };
      img.src = reader.result;
    };
    reader.onerror = () => {
      setError('Fehler beim Lesen der Datei');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setFormData({ ...formData, logo: null });
    setLogoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    try {
      const submitData = {
        ...formData,
        snapshotRetention: formData.sicherungRetention
      };
      delete submitData.sicherungRetention;
      const response = await api.put(`/tenants/${tenantId}`, submitData, null);
      if (response) {
        await loadTenant();
        setSuccessMessage('Einstellungen erfolgreich gespeichert');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Fehler beim Speichern';
      setError(`Fehler beim Speichern: ${errorMessage}`);
    }
  };

  if (loading) {
    return (
      <div className="settings">
        <div className="page-header">
          <h1>Einstellungen</h1>
        </div>
        <Spinner />
      </div>
    );
  }

  if (error && !tenant) {
    return (
      <div className="settings">
        <div className="page-header">
          <h1>Einstellungen</h1>
        </div>
        <div style={{ color: 'red' }}>{error}</div>
      </div>
    );
  }

  return (
    <div className="settings">
      <div className="page-header">
        <h1>Einstellungen</h1>
      </div>

      <div className="settings-content">
        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Unternehmenslogo</label>
            {logoPreview && (
              <div style={{ marginBottom: '1rem' }}>
                <img 
                  src={logoPreview} 
                  alt="Logo Vorschau" 
                  style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain', border: '1px solid #ddd', padding: '0.5rem' }}
                />
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="btn-secondary btn-small"
                  style={{ marginLeft: '1rem' }}
                >
                  Logo entfernen
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
            />
            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
              Das Logo wird auf Rechnungen angezeigt. Empfohlene Formate: PNG, JPG. Max. 5MB (wird automatisch komprimiert).
            </p>
          </div>

          <div className="form-group">
            <label>Steuersystem</label>
            <select
              value={formData.taxSystem}
              onChange={(e) => setFormData({ ...formData, taxSystem: e.target.value })}
            >
              <option value="DE">Deutschland (19% MwSt.)</option>
              <option value="CH">Schweiz (7.7% MwSt.)</option>
            </select>
          </div>

          <h2>Adresse</h2>

          <div className="form-group">
            <label>Straße</label>
            <input
              type="text"
              value={formData.address.street}
              onChange={(e) => setFormData({
                ...formData,
                address: { ...formData.address, street: e.target.value }
              })}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>PLZ</label>
              <input
                type="text"
                value={formData.address.zip}
                onChange={(e) => setFormData({
                  ...formData,
                  address: { ...formData.address, zip: e.target.value }
                })}
              />
            </div>
            <div className="form-group">
              <label>Stadt</label>
              <input
                type="text"
                value={formData.address.city}
                onChange={(e) => setFormData({
                  ...formData,
                  address: { ...formData.address, city: e.target.value }
                })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Land</label>
            <input
              type="text"
              value={formData.address.country}
              onChange={(e) => setFormData({
                ...formData,
                address: { ...formData.address, country: e.target.value }
              })}
            />
          </div>

          <h2>Steuersystem</h2>

          {formData.taxSystem === 'DE' && (
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isKleinunternehmer || false}
                  onChange={(e) => setFormData({ ...formData, isKleinunternehmer: e.target.checked })}
                />
                Kleinunternehmer nach §19 UStG
              </label>
              <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                Als Kleinunternehmer sind Sie von der Umsatzsteuer befreit. Auf Rechnungen wird der Hinweis nach §19 UStG angezeigt.
              </p>
            </div>
          )}

          {formData.taxSystem === 'CH' && (
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isNotVatLiable || false}
                  onChange={(e) => setFormData({ ...formData, isNotVatLiable: e.target.checked })}
                />
                Nicht MWST-pflichtig (Jahresumsatz unter 100.000 CHF)
              </label>
              <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                Unternehmen mit einem Jahresumsatz von weniger als 100.000 CHF sind von der Mehrwertsteuerpflicht befreit. Auf Rechnungen wird der Hinweis "Nicht MWST-pflichtig" angezeigt.
              </p>
            </div>
          )}

          <h2>Rechnung</h2>

          <div className="form-group">
            <label>Rechnungstext (unten auf der Rechnung)</label>
            <textarea
              value={formData.invoiceFooterText || ''}
              onChange={(e) => setFormData({ ...formData, invoiceFooterText: e.target.value })}
              rows="3"
              placeholder="Vielen Dank für Ihren Auftrag!"
            />
            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
              Dieser Text erscheint am Ende der Rechnung vor dem Footer.
            </p>
          </div>

          <h2>Kunden Self-Service</h2>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.customerSelfServiceEnabled}
                onChange={(e) => setFormData({ ...formData, customerSelfServiceEnabled: e.target.checked })}
              />
              Kunden Self-Service aktivieren
            </label>
            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
              Ermöglicht Kunden über QR-Code auf Rechnungen Zugriff auf ihre Daten und Terminbuchung
            </p>
          </div>

          {formData.taxSystem === 'DE' && (
            <>
              <h2>Impressum (für Rechnungen)</h2>
              <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                Diese Angaben erscheinen im Footer der Rechnung (unten links).
              </p>

              <div className="form-row">
                <div className="form-group">
                  <label>Steuernummer {formData.taxSystem === 'CH' ? '(MWST-Nummer)' : ''}</label>
                  <input
                    type="text"
                    value={formData.imprint.taxNumber || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      imprint: { ...formData.imprint, taxNumber: e.target.value }
                    })}
                    placeholder={formData.taxSystem === 'CH' ? 'z.B. CHE-123.456.789 MWST' : 'z.B. 12/345/67890'}
                  />
                  {formData.taxSystem === 'CH' && (
                    <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                      Format: CHE-XXX.XXX.XXX MWST (nur bei MWST-Pflicht erforderlich)
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label>USt-IdNr. (optional)</label>
                  <input
                    type="text"
                    value={formData.imprint.vatIdNumber || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      imprint: { ...formData.imprint, vatIdNumber: e.target.value }
                    })}
                    placeholder="z.B. DE123456789"
                  />
                  <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                    Alternativ zur Steuernummer verwendbar
                  </p>
                </div>
              </div>

              <div className="form-group">
                <label>Gerichtsstandort</label>
                <input
                  type="text"
                  value={formData.imprint.courtLocation || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    imprint: { ...formData.imprint, courtLocation: e.target.value }
                  })}
                  placeholder="z.B. Berlin"
                />
              </div>

              <div className="form-group">
                <label>Geschäftsführer</label>
                <input
                  type="text"
                  value={formData.imprint.managingDirector || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    imprint: { ...formData.imprint, managingDirector: e.target.value }
                  })}
                  placeholder="z.B. Max Mustermann"
                />
              </div>

              <div className="form-group">
                <label>Telefon</label>
                <input
                  type="text"
                  value={formData.imprint.phone || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    imprint: { ...formData.imprint, phone: e.target.value }
                  })}
                  placeholder="z.B. +49 30 12345678"
                />
              </div>

              <div className="form-group">
                <label>E-Mail</label>
                <input
                  type="email"
                  value={formData.imprint.email || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    imprint: { ...formData.imprint, email: e.target.value }
                  })}
                  placeholder="z.B. info@example.com"
                />
              </div>

              <div className="form-group">
                <label>Website (optional)</label>
                <input
                  type="text"
                  value={formData.imprint.website || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    imprint: { ...formData.imprint, website: e.target.value }
                  })}
                  placeholder="z.B. www.example.com"
                />
              </div>
            </>
          )}

          <h2>Bankverbindung</h2>

          <div className="form-group">
            <label>Bankname</label>
            <input
              type="text"
              value={formData.bankData.bankName}
              onChange={(e) => setFormData({
                ...formData,
                bankData: { ...formData.bankData, bankName: e.target.value }
              })}
            />
          </div>

          <div className="form-group">
            <label>IBAN</label>
            <input
              type="text"
              value={formData.bankData.iban}
              onChange={(e) => setFormData({
                ...formData,
                bankData: { ...formData.bankData, iban: e.target.value.toUpperCase() }
              })}
            />
          </div>

          <div className="form-group">
            <label>BIC</label>
            <input
              type="text"
              value={formData.bankData.bic}
              onChange={(e) => setFormData({
                ...formData,
                bankData: { ...formData.bankData, bic: e.target.value.toUpperCase() }
              })}
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">
              Speichern
            </button>
          </div>
        </form>

        <h2>Sicherungen</h2>
        
        <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Sicherungs-Aufbewahrung (in Tagen)</h3>
          <p style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '1rem' }}>
            Legen Sie fest, wie lange Sicherungen der verschiedenen Häufigkeiten aufbewahrt werden sollen.
          </p>
          <div className="form-row">
            <div className="form-group">
              <label>Stündlich</label>
              <input
                type="number"
                min="1"
                value={formData.sicherungRetention.hourly}
                onChange={(e) => setFormData({
                  ...formData,
                  sicherungRetention: {
                    ...formData.sicherungRetention,
                    hourly: parseInt(e.target.value) || 7
                  }
                })}
              />
            </div>
            <div className="form-group">
              <label>Täglich</label>
              <input
                type="number"
                min="1"
                value={formData.sicherungRetention.daily}
                onChange={(e) => setFormData({
                  ...formData,
                  sicherungRetention: {
                    ...formData.sicherungRetention,
                    daily: parseInt(e.target.value) || 30
                  }
                })}
              />
            </div>
            <div className="form-group">
              <label>Wöchentlich</label>
              <input
                type="number"
                min="1"
                value={formData.sicherungRetention.weekly}
                onChange={(e) => setFormData({
                  ...formData,
                  sicherungRetention: {
                    ...formData.sicherungRetention,
                    weekly: parseInt(e.target.value) || 90
                  }
                })}
              />
            </div>
            <div className="form-group">
              <label>Monatlich</label>
              <input
                type="number"
                min="1"
                value={formData.sicherungRetention.monthly}
                onChange={(e) => setFormData({
                  ...formData,
                  sicherungRetention: {
                    ...formData.sicherungRetention,
                    monthly: parseInt(e.target.value) || 365
                  }
                })}
              />
            </div>
          </div>
        </div>
        
        <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setShowSicherungModal(true)}
            className="btn-primary"
          >
            Sicherung erstellen
          </button>
          <label className="btn-secondary" style={{ cursor: 'pointer', display: 'inline-block' }}>
            Sicherung importieren
            <input
              type="file"
              accept=".tar.gz,.tgz"
              onChange={handleImportSicherung}
              disabled={importingSicherung}
              style={{ display: 'none' }}
            />
          </label>
          {importingSicherung && <span style={{ lineHeight: '2.5rem' }}>Importiere...</span>}
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3>Sicherungs-Zeitplan</h3>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={sicherungSchedule.enabled}
                onChange={(e) => setSicherungSchedule({ ...sicherungSchedule, enabled: e.target.checked })}
              />
              Automatische Sicherungen aktivieren
            </label>
          </div>
          {sicherungSchedule.enabled && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Häufigkeit</label>
                  <select
                    value={sicherungSchedule.frequency}
                    onChange={(e) => setSicherungSchedule({ ...sicherungSchedule, frequency: e.target.value })}
                  >
                    <option value="hourly">Stündlich</option>
                    <option value="daily">Täglich</option>
                    <option value="weekly">Wöchentlich</option>
                    <option value="monthly">Monatlich</option>
                  </select>
                </div>
                {sicherungSchedule.frequency !== 'hourly' && (
                  <div className="form-group">
                    <label>Uhrzeit</label>
                    <input
                      type="time"
                      value={sicherungSchedule.time}
                      onChange={(e) => setSicherungSchedule({ ...sicherungSchedule, time: e.target.value })}
                    />
                  </div>
                )}
                {sicherungSchedule.frequency === 'hourly' && (
                  <div className="form-group">
                    <label>Minute</label>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={sicherungSchedule.time ? sicherungSchedule.time.split(':')[1] || '0' : '0'}
                      onChange={(e) => {
                        const minutes = e.target.value.padStart(2, '0');
                        setSicherungSchedule({ ...sicherungSchedule, time: `00:${minutes}` });
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="form-actions">
                <button type="button" onClick={handleSaveSchedule} className="btn-primary">
                  Zeitplan speichern
                </button>
              </div>
            </>
          )}
        </div>

        {showTestDataButton && (
          <div style={{ marginTop: '2rem', padding: '1.5rem', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px' }}>
            <h2 style={{ marginTop: 0 }}>Testdaten</h2>
            <p style={{ marginBottom: '1rem', color: '#856404' }}>
              Im Debug-Modus können Sie die Testdaten neu erstellen. Alle vorhandenen Testdaten werden überschrieben.
            </p>
            <button
              type="button"
              onClick={handleRecreateTestData}
              className="btn-primary"
              disabled={recreatingTestData}
            >
              {recreatingTestData ? 'Testdaten werden erstellt...' : 'Testdaten neu erstellen'}
            </button>
          </div>
        )}

        <div>
          <h3>Vorhandene Sicherungen</h3>
          {sicherungen.length === 0 ? (
            <p>Keine Sicherungen vorhanden</p>
          ) : (
            frequencies.map(frequency => {
              const sicherungenInGroup = groupedSicherungen[frequency] || [];
              if (sicherungenInGroup.length === 0) return null;
              
              return (
                <div key={frequency} style={{ marginBottom: '2rem' }}>
                  <h4 style={{ marginBottom: '1rem', color: '#4a5568', fontSize: '1.1rem' }}>
                    {getFrequencyLabel(frequency)} ({sicherungenInGroup.length})
                  </h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#f7fafc' }}>
                        <th style={{ textAlign: 'left', padding: '0.75rem' }}>Name</th>
                        <th style={{ textAlign: 'left', padding: '0.75rem' }}>Erstellt am</th>
                        <th style={{ textAlign: 'right', padding: '0.75rem' }}>Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sicherungenInGroup.map(sicherung => (
                        <tr key={sicherung.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '0.75rem' }}>{sicherung.name}</td>
                          <td style={{ padding: '0.75rem' }}>
                            {new Date(sicherung.createdAt).toLocaleString('de-DE')}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                            <button
                              type="button"
                              onClick={() => handleExportSicherung(sicherung.id)}
                              className="btn-secondary btn-small"
                              style={{ marginRight: '0.5rem' }}
                            >
                              Export
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRestoreSicherung(sicherung.id)}
                              className="btn-secondary btn-small"
                              style={{ marginRight: '0.5rem' }}
                              disabled={restoreSicherungId === sicherung.id}
                            >
                              {restoreSicherungId === sicherung.id ? 'Wird wiederhergestellt...' : 'Wiederherstellen'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteSicherung(sicherung.id)}
                              className="btn-danger btn-small"
                            >
                              Löschen
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })
          )}
        </div>
      </div>

      {showSicherungModal && (
        <div className="modal" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', maxWidth: '500px', width: '90%' }}>
            <h2>Sicherung erstellen</h2>
            <div className="form-group">
              <label>Name (optional)</label>
              <input
                type="text"
                value={sicherungName}
                onChange={(e) => setSicherungName(e.target.value)}
                placeholder="Sicherung Name"
              />
            </div>
            <div className="form-actions">
              <button type="button" onClick={handleTakeSicherung} className="btn-primary">
                Erstellen
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowSicherungModal(false);
                  setSicherungName('');
                }}
                className="btn-secondary"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {user && user.role === 'admin' && tenant && (
        <section className="settings-section" style={{ marginTop: '2rem', border: '2px solid #dc3545', borderRadius: '8px', padding: '1.5rem', backgroundColor: '#fff5f5' }}>
          <h2 style={{ color: '#dc3545', marginTop: 0 }}>Tenant löschen</h2>
          <p style={{ color: '#721c24', marginBottom: '1rem' }}>
            <strong>Warnung:</strong> Diese Aktion löscht alle Daten, Backups und Benutzer dieses Tenants unwiderruflich!
          </p>
          
          {tenant.deletionScheduled && !tenant.deletionCancelled && (
            <div style={{ padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '4px', marginBottom: '1rem', border: '1px solid #ffc107' }}>
              <p style={{ margin: 0, fontWeight: 'bold', color: '#856404' }}>
                ⚠️ Löschung terminiert für: {new Date(tenant.deletionScheduledFor).toLocaleString('de-DE')}
              </p>
              <button
                type="button"
                onClick={handleCancelDeletion}
                className="btn-secondary"
                style={{ marginTop: '0.5rem' }}
              >
                Löschung abbrechen
              </button>
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setShowDeletionModal(true)}
              className="btn-danger"
              disabled={tenant.deletionScheduled && !tenant.deletionCancelled}
            >
              Tenant löschen terminieren
            </button>
          </div>
        </section>
      )}

      {showDeletionModal && (
        <div className="modal" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div className="modal-content" style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', maxWidth: '600px', width: '90%', border: '2px solid #dc3545' }}>
            <h2 style={{ color: '#dc3545', marginTop: 0 }}>Tenant löschen</h2>
            <p style={{ color: '#721c24', marginBottom: '1.5rem' }}>
              <strong>Warnung:</strong> Diese Aktion löscht alle Daten, Backups und Benutzer dieses Tenants unwiderruflich!
            </p>
            
            <div className="form-group">
              <label>Tenant-Name zur Bestätigung</label>
              <input
                type="text"
                value={deletionTenantName}
                onChange={(e) => setDeletionTenantName(e.target.value)}
                placeholder={tenant?.name}
                style={{ borderColor: '#dc3545' }}
              />
            </div>

            <div className="form-group">
              <label>Admin-Passwort</label>
              <input
                type="password"
                value={deletionAdminPassword}
                onChange={(e) => setDeletionAdminPassword(e.target.value)}
                placeholder="Admin-Passwort eingeben"
                style={{ borderColor: '#dc3545' }}
              />
            </div>

            <div className="form-group">
              <label>Löschung terminieren (Stunden)</label>
              <select
                value={deletionDelayHours}
                onChange={(e) => setDeletionDelayHours(Number(e.target.value))}
                style={{ borderColor: '#dc3545' }}
              >
                <option value={24}>24 Stunden</option>
                <option value={48}>48 Stunden</option>
              </select>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={handleScheduleDeletion}
                className="btn-danger"
                disabled={deletingTenant || deletionTenantName !== tenant?.name || !deletionAdminPassword}
              >
                {deletingTenant ? 'Wird terminiert...' : 'Löschung terminieren'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowDeletionModal(false);
                  setDeletionTenantName('');
                  setDeletionAdminPassword('');
                  setDeletionDelayHours(24);
                }}
                className="btn-secondary"
                disabled={deletingTenant}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;

